import 'src/config/axios'
import 'antd/dist/antd.css'
import 'src/assets/scss/main.scss'
import App from 'next/app'
import store from '../app/store'
import { Provider } from 'react-redux'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import { Layout, AuthLayout } from '../components'
import type { AppContext, AppProps } from 'next/app'
import { Country, HeaderState } from 'src/core/models'
import { getCountryListAPI, getHeaderData } from 'src/features'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import ExplorerLayout from 'src/components/layout/ExplorerLayout'

interface MyAppProps extends AppProps {
  headerData: HeaderState
  countries: Country[]
}

// Create a client
const queryClient = new QueryClient()

const MyApp = ({ Component, pageProps, headerData, countries }: MyAppProps) => {
  const router = useRouter()
  const isAuthRoute = router?.asPath?.includes('auth')
  const isExplorerRoute =  router.pathname.includes('explorer')

  if(isExplorerRoute) {
    return (<ExplorerLayout>
      <Component {...pageProps} />
    </ExplorerLayout>)
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        {isAuthRoute ? (
          <AuthLayout
            headerData={headerData}
            countries={countries}
          >
            <Component {...pageProps} />
          </AuthLayout>
        ) : (
          <Layout
            countries={countries}
            headerData={headerData}
            required={pageProps.required}
            protected={pageProps.protected}
            isAdminOrDeveloper={pageProps.isAdminOrDeveloper}
          >
            <Component {...pageProps} />
          </Layout>
        )}
      </Provider>
    </QueryClientProvider>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {

  const appProps = await App.getInitialProps(appContext)

  const headerData = await getHeaderData(appContext.router.locale)
  const countries = await getCountryListAPI()

  return { ...appProps, headerData, countries }
}

export default appWithTranslation(MyApp)
